import React from "react";
import PropTypes from "prop-types";
import { Loader } from "rsuite";
import { ROW_KIND, MAX_DEPTH_CLASS } from "../constants";

const columnsPropType = PropTypes.arrayOf(
  PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    className: PropTypes.string,
    headerRender: PropTypes.func,
    render: PropTypes.func,
  })
).isRequired;

// ARIA roles mirror table semantics on a flex/div layout (Figma uses divs,
// not <table>). The flex layout is what enables Figma-accurate spacing.
const TableHeader = ({ columns }) => (
  <div className="rpdr-table__header" role="row">
    {columns.map((col) => (
      <div
        key={col.key}
        role="columnheader"
        className={`rpdr-table__th ${col.className || ""}`}
      >
        {col.headerRender ? col.headerRender() : col.label}
      </div>
    ))}
  </div>
);

TableHeader.propTypes = {
  columns: columnsPropType,
};

const rowKindClass = (row) => {
  if (row.kind === ROW_KIND.BASE) {
    return row.isExpanded && row.hasChildren
      ? "rpdr-table__row--base-expanded"
      : "rpdr-table__row--base-collapsed";
  }
  return "rpdr-table__row--derived";
};

const TableRow = ({ row, columns }) => {
  const depth = Math.min(row.depth || 0, MAX_DEPTH_CLASS);
  const rowClasses = [
    "rpdr-table__row",
    `rpdr-table__row--depth-${depth}`,
    rowKindClass(row),
    row.isGapAbove && "rpdr-table__row--gap-above",
    row.isLastSibling && "rpdr-table__row--last-sibling",
    row.isDisabled && "rpdr-table__row--disabled",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rowClasses} role="row">
      {columns.map((col) => (
        <div
          key={col.key}
          role="cell"
          className={`rpdr-table__td ${col.className || ""}`}
        >
          {col.render ? col.render(row) : row[col.key]}
        </div>
      ))}
      {/* Ancestor rails rendered AFTER cells so:
       *  (1) `.rpdr-table__td:first-child` still matches the code cell,
       *      keeping the depth-N margin-left override intact.
       *  (2) Spans paint after cells, so the rail sits above any cell bg. */}
      {row.ancestorRailDepths?.map((d) => (
        <span
          key={`anc-${d}`}
          aria-hidden="true"
          className={`rpdr-table__ancestor-rail rpdr-table__ancestor-rail--at-${d}`}
        />
      ))}
    </div>
  );
};

TableRow.propTypes = {
  row: PropTypes.shape({
    kind: PropTypes.string.isRequired,
    depth: PropTypes.number,
    isExpanded: PropTypes.bool,
    hasChildren: PropTypes.bool,
    isGapAbove: PropTypes.bool,
    isLastSibling: PropTypes.bool,
    ancestorRailDepths: PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
  columns: columnsPropType,
};

const RatePlansDerivedRateTable = ({
  columns,
  data,
  loading,
  loadingMessage,
  emptyMessage,
  className,
}) => {
  const isEmpty = !loading && data.length === 0;
  const rootClass = ["rpdr-table", className].filter(Boolean).join(" ");

  return (
    <>
      <hr className="rpdr-table__divider rpdr-table__divider-top" />
      <div className="rpdr-table__scroll">
        <div className={rootClass} role="table">
          <TableHeader columns={columns} />

          {loading && (
            <div className="rpdr-table__loader">
              <Loader size="md" content={loadingMessage} />
            </div>
          )}

          {isEmpty && <div className="rpdr-table__empty">{emptyMessage}</div>}

          {!loading && !isEmpty && (
            <div className="rpdr-table__body">
              {data.map((row) => (
                <TableRow
                  key={`${row.kind}-${row.id}`}
                  row={row}
                  columns={columns}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      <hr className="rpdr-table__divider rpdr-table__divider-bottom" />
    </>
  );
};

RatePlansDerivedRateTable.propTypes = {
  columns: columnsPropType,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  loadingMessage: PropTypes.string,
  emptyMessage: PropTypes.string,
  className: PropTypes.string,
};

export default RatePlansDerivedRateTable;
